/**
 * Solera SSO handoff timing (ENT-2926). App-wide RUM monitors live in
 * datadog/monitors/; this file is the Solera signal.
 *
 * Measures one interval per Solera SSO page load: from RUM init until the
 * reads the landing needs have settled (UPID validation + enrollment info,
 * then the eligibility lookup). It deliberately stops before
 * `WelcomeToNoomAnimation` finishes: that animation is fixed CSS (~7.5 s, or
 * ~12.5 s when eligible) and would breach 5 s on every healthy landing.
 *
 * This is an internal WEA budget, not the contractual 10 s handoff failure.
 * It cannot see Solera, the SAML hop, or the cross-origin redirect; those are
 * monitored in b2b-saml-enrollment. The Solera monitors in both repos email
 * ops on alert, and their event history is the quarterly audit log.
 *
 * Nothing is emitted for any other partner or flow.
 * The prod monitor is datadog/monitors/rum-solera-handoff.prod.json.
 */

import { leadSourceFromSearch, recordDurationVital } from "./datadog";

export const SOLERA_HANDOFF_VITAL = "enrollment.solera_handoff";
const DEFINITION_VERSION = 1;
export const SOLERA_HANDOFF_TIMEOUT_MS = 15000;

// Same partner keys as the b2b-saml-enrollment Solera monitors.
const SOLERA_PARTNER_SLUGS = new Set(["solera", "solerastaging"]);

const EMPLOYER_LANDING = /^\/employer\/[^/]+\/?$/;

export type SoleraHandoffOutcome = "ready" | "error" | "timeout";
/** `blocked` = the read finished with a business result (claimed code, ...). */
export type SoleraReadResult = "ready" | "blocked" | "error";

type HandoffState = {
  startMs: number;
  partnerId?: number;
  upidSettled: boolean;
  timer: ReturnType<typeof setTimeout>;
};

let handoff: HandoffState | null = null;

function finish(outcome: SoleraHandoffOutcome, durationMs: number): void {
  if (!handoff) return;
  const { partnerId, timer } = handoff;
  clearTimeout(timer);
  handoff = null;
  if (partnerId === undefined) return;
  recordDurationVital(SOLERA_HANDOFF_VITAL, durationMs, {
    outcome,
    partnerId,
    definitionVersion: DEFINITION_VERSION,
  });
}

function elapsedMs(): number {
  return handoff ? performance.now() - handoff.startMs : 0;
}

/**
 * Starts the clock when this page load is an SSO landing from SAML
 * (`/employer/{slug}?leadSource=sso&upid=...&eligibilityId=...`). Partner is
 * not known yet, so nothing is sent until `noteSoleraPartner` confirms Solera.
 * Call once from `bootstrap()`, after `initializeDatadog()`.
 */
export function armSoleraHandoffClock(): void {
  if (handoff) return;
  const { pathname, search } = window.location;
  const params = new URLSearchParams(search);
  if (
    !EMPLOYER_LANDING.test(pathname) ||
    leadSourceFromSearch(search) !== "sso" ||
    !params.get("upid") ||
    !params.get("eligibilityId") ||
    params.get("sharedEligibility") === "true"
  ) {
    return;
  }
  handoff = {
    startMs: performance.now(),
    upidSettled: false,
    timer: setTimeout(
      () => finish("timeout", SOLERA_HANDOFF_TIMEOUT_MS),
      SOLERA_HANDOFF_TIMEOUT_MS,
    ),
  };
}

/**
 * Partner lookup settled. A non-Solera partner, or a failed lookup that cannot
 * be attributed to Solera, drops the measurement without sending anything.
 */
export function noteSoleraPartner(
  partner: { id: number; slug: string } | null,
): void {
  if (!handoff || handoff.partnerId !== undefined) return;
  if (!partner || !SOLERA_PARTNER_SLUGS.has(partner.slug)) {
    clearTimeout(handoff.timer);
    handoff = null;
    return;
  }
  handoff.partnerId = partner.id;
}

/** UPID validation + enrollment info settled. */
export function noteSoleraUpid(result: SoleraReadResult): void {
  if (!handoff || handoff.partnerId === undefined || handoff.upidSettled) {
    return;
  }
  if (result === "ready") {
    handoff.upidSettled = true;
    return;
  }
  // Without enrollment info the eligibility lookup never runs; finish here.
  finish(result === "error" ? "error" : "ready", elapsedMs());
}

/** SSO eligibility lookup settled. */
export function noteSoleraEligibility(result: SoleraReadResult): void {
  if (!handoff || handoff.partnerId === undefined) return;
  finish(result === "error" ? "error" : "ready", elapsedMs());
}

/** Test-only: drops any in-flight measurement. */
export function resetSoleraHandoffForTesting(): void {
  if (handoff) clearTimeout(handoff.timer);
  handoff = null;
}
